import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, GraduationCap, BookOpen, Dumbbell, ClipboardList, MapPin, ArrowRight, Layers } from "lucide-react";
import { toast } from "sonner";

const LEVELS = [
  { value: "puppy", label: "Puppy" }, { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" }, { value: "advanced", label: "Advanced" },
  { value: "specialist", label: "Specialist" },
];
const STATUSES = [
  { value: "draft", label: "Draft" }, { value: "active", label: "Active" }, { value: "archived", label: "Archived" },
];
const VISIBILITY = [
  { value: "internal", label: "Internal only" }, { value: "trainer", label: "Trainer visible" }, { value: "client", label: "Client visible" },
];
const empty = { name: "", level: "puppy", description: "", goal: "", estimated_duration: "", status: "draft", visibility: "trainer" };

export default function Programs() {
  const { user } = useAuth();
  const ref = useRefData();
  const navigate = useNavigate();
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(user?.role);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { const r = await api.get("/programs"); setPrograms(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const r = await api.post("/programs", form);
      toast.success("Program created"); setOpen(false); await load(); ref.reload();
      navigate(`/programs/${r.data.id}`);
    } catch (e) { toast.error("Create failed"); }
    setSaving(false);
  };

  const filtered = (s) => s === "all" ? programs : programs.filter((p) => (p.status || "active") === s);
  const stat = (key) => programs.reduce((a, p) => a + (p[key] || 0), 0);

  const ProgramCard = ({ p }) => (
    <Card className="border-border hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer group" onClick={() => navigate(`/programs/${p.id}`)} data-testid="program-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-neutral-900 flex items-center justify-center"><GraduationCap size={20} className="text-primary-foreground" strokeWidth={1.5} /></div>
            <div>
              <div className="font-heading font-semibold text-lg leading-tight">{p.name}</div>
              <div className="flex items-center gap-1.5 mt-1"><StatusBadge value={p.level} /><StatusBadge value={p.status || "active"} /></div>
            </div>
          </div>
          <ArrowRight size={18} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.description || p.goal || "No description."}</p>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <Stat icon={BookOpen} n={p.lessons_count} label="Lessons" />
          <Stat icon={Dumbbell} n={p.exercises_count} label="Exercises" />
          <Stat icon={ClipboardList} n={p.homework_count} label="Homework" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/60 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><MapPin size={12} /> Used by {p.classes_count} class{p.classes_count === 1 ? "" : "es"}</span>
          <span>Updated {fmtDate(p.updated_at || p.created_at)}</span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <PageHeader
        title="HV Method"
        subtitle="The Hundeverket training method — standardized programs, lessons, exercises and homework across the franchise."
        action={canManage && <Button onClick={() => { setForm(empty); setOpen(true); }} className="gap-2" data-testid="programs-add-btn"><Plus size={16} strokeWidth={1.5} /> New Program</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Summary icon={Layers} n={programs.length} label="Programs" />
        <Summary icon={BookOpen} n={stat("lessons_count")} label="Lessons" />
        <Summary icon={Dumbbell} n={stat("exercises_count")} label="Exercises" />
        <Summary icon={ClipboardList} n={stat("homework_count")} label="Homework Templates" />
      </div>

      <Tabs defaultValue="all">
        <TabsList className="h-auto">
          <TabsTrigger value="all" data-testid="programs-tab-all">All <span className="ml-1.5 text-xs text-muted-foreground">({programs.length})</span></TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} data-testid={`programs-tab-${s.value}`}>{s.label} <span className="ml-1.5 text-xs text-muted-foreground">({filtered(s.value).length})</span></TabsTrigger>
          ))}
        </TabsList>
        {["all", ...STATUSES.map((s) => s.value)].map((s) => (
          <TabsContent key={s} value={s} className="mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered(s).map((p) => <ProgramCard key={p.id} p={p} />)}
              {filtered(s).length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No programs here.</p>}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Training Program</DialogTitle>
            <DialogDescription>Define the program shell — add lessons, exercises and homework on the next screen.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Program name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="program-field-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="program-field-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Estimated duration</Label><Input value={form.estimated_duration} onChange={(e) => setForm({ ...form, estimated_duration: e.target.value })} placeholder="e.g. 5 weeks" data-testid="program-field-duration" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="program-field-description" /></div>
            <div className="grid gap-1.5"><Label>Goal of program</Label><Textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="program-field-goal" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="program-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5"><Label>Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger data-testid="program-field-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBILITY.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.name} data-testid="program-save-btn">{saving ? "Creating..." : "Create program"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, n, label }) => (
  <div className="rounded-lg bg-muted/50 py-2">
    <div className="flex items-center justify-center gap-1 text-foreground"><Icon size={13} className="text-primary" /> <span className="font-heading font-semibold">{n ?? 0}</span></div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
  </div>
);

const Summary = ({ icon: Icon, n, label }) => (
  <Card className="border-border"><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-1.5"><Icon size={15} strokeWidth={1.5} /><span className="text-[11px] uppercase tracking-wide font-semibold">{label}</span></div>
    <div className="text-2xl font-heading font-semibold tracking-tight">{n}</div>
  </CardContent></Card>
);
